
 /*
  *****************************************************************************
  * WARNING: This file is automatically generated.
  * Do not edit this file manually.
  *****************************************************************************
  *
  * The source file is located at:
  *   /codebuild/output/src2723206075/src/vl-app/pages/patients/index.tsx
  */

import { createFileRoute } from '@tanstack/react-router';
import React, { useEffect, useState } from 'react';
import { Button, Card, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { useInitialisedStore } from '@hooks/useInitialisedStore';
import { WarningOutlined } from 'antd/icons';
import WelcomeDashboard from '@components/Patients/Welcome/Welcome';
import ConfluenceArticle from '@components/UI/ConfluenceArticle';
import PatientMediaPermissionsWarning from '@components/MediaPermissions/PatientMediaPermissionsWarning';
import useRuntimeConfig from '@vl-core/hooks/useConfig';
import useAuth from '@vl-core/useAuth';
import useIsRestricted from '@hooks/useIsRestricted';
import NextSteps from '@components/Patients/ToDos/NextSteps';
import styled from '@emotion/styled';
import StartNextSurveyModal from '@components/Modal/StartNextSurveyModal';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@hooks/useRouter';
import { useDispatch } from 'react-redux';
import * as actions from '@actions';
import { TransferMessageBody, useAutoSSOWithTransferModal } from '@hooks/useAutoSSO';
import useUpcomingAppointment from '@hooks/useUpcomingAppointment';
import Header from '@components/Layouts/Header/Header';
import DocmanGPRejectedModal from '@components/Patients/Docman/DocmanGPRejectedModal';
import UpcomingAppointment from '@components/Patients/Welcome/UpcomingAppointment';

const WarningCard = styled(Card)`
  margin-bottom: 1rem;

  .warning-slider-head {
    padding: 7px;
    border-bottom: 2px solid var(--productPrimary);
    color: var(--black);
    font-size: 22px;
  }

  .pp-img-wrap {
    display: inline-block;
    width: 25px;
    text-align: center;
    color: var(--productPrimary);
  }

  .slider-detail {
    margin-top: 13px;
    padding: 0;
    font-size: 15px;
    color: var(--black);
    line-height: 16px;
    list-style: none;

    > li {
      margin-bottom: 8px;

      &:last-child {
        margin: 0;
      }
    }
  }
`;

const ProductBanner = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100px;
  background-image: url('https://assets.hbsuk.co.uk/img/platform/HBSUK_ConnectingLine_03_Comp.png');
  background-repeat: no-repeat;
  background-size: 150% 125%;
  background-position: center;

  @media (min-width: 768px) {
    display: none;
  }
`;

const TodoSectionWrapper = styled.div`
  margin-bottom: 1rem;
`;

const NextStepsCard = styled(Card)`
  width: 100%;
  border-radius: 0.625rem;
  box-shadow: 0 3px 6px #00000029;

  .ant-card-body {
    padding: 15px;

    @media (min-width: 992px) {
      padding: 15px 30px;
    }
  }
`;

const UninsuredWarning = () => {
  const { user } = useAuth();
  const { t } = useTranslation('common');
  return (
    <WarningCard>
      {user ? (
        <div>
          <div className="warning-slider-head">
            <span className="pp-img-wrap">
              <WarningOutlined />
            </span>
            &nbsp;
            <strong>{t('Warning')}</strong>
          </div>
          <div className="slider-detail">
            <p>{t('UnInsuredWarningMessage')}</p>
          </div>
        </div>
      ) : (
        ''
      )}
    </WarningCard>
  );
};

const welcomeModalShow = () => localStorage.getItem('welcomeModalShown') === '1';
const setWelcomeModalShow = (value: '1' | '0') => localStorage.setItem('welcomeModalShown', value);
const firstTimeLoginAfterAcceptingPolicy = () => !!sessionStorage.getItem('firstTimeLoginAfterAcceptingPolicy');

const Patients = () => {
  const { t } = useTranslation('common');
  const { TOUR_PROVIDER, DISABLE_WELCOME_POPUP } = useRuntimeConfig();
  const [importantInfo, setImportantInfo] = useState(
    (firstTimeLoginAfterAcceptingPolicy() || !welcomeModalShow()) && !TOUR_PROVIDER
  );
  const initialised = useInitialisedStore();
  const { user } = useAuth();
  const [selfReferralID, setSelfReferralID] = useState<number | null>(null);
  const [showNextSurveyModal, setShowNextSurveyModal] = useState(false);
  const isRestricted = useIsRestricted();
  const { upcomingAppointment, refetchUpcomingAppointment } = useUpcomingAppointment();
  const queryClient = useQueryClient();

  useAutoSSOWithTransferModal(
    <TransferMessageBody>
      <p>{t("Welcome back! To book an appointment, please click 'Get expert help for a new co")}</p>
    </TransferMessageBody>
  );

  useEffect(() => {
    if (selfReferralID) {
      queryClient.invalidateQueries({ queryKey: ['fetchAllCases'] }).then();
      queryClient.invalidateQueries({ queryKey: ['getNextSteps'] }).then();
      setShowNextSurveyModal(true);
    }
  }, [selfReferralID]);

  const handleModalCancel = () => {
    sessionStorage.removeItem('firstTimeLoginAfterAcceptingPolicy');
    setWelcomeModalShow('1');
    setImportantInfo(false);
    const videoIframes = document.getElementsByTagName('iframe');
    Array.from(videoIframes).forEach((frame) => {
      frame.src = '';
      return frame;
    });
  };

  if (!initialised) {
    return <p>{t('Initialising ...')}</p>;
  }

  return (
    <div className="page-container">
      <Header pageType="patients" pageTitle="Patient" />
      <section className="section-layout patient dashboard">
        <ProductBanner className="product-banner" role="presentation" />
        <main className="container" id="page-content">
          <PatientMediaPermissionsWarning />
          <WelcomeDashboard
            onSelfRefer={(val) => {
              setSelfReferralID(val);
              sessionStorage.removeItem('case-filter-selected');
            }}
          />
          {user?.insured && upcomingAppointment && (
            <UpcomingAppointment appointment={upcomingAppointment} refetch={refetchUpcomingAppointment} />
          )}
          {!user?.insured && <UninsuredWarning />}
          <TodoSectionWrapper>
            <NextStepsCard>
              <NextSteps newCase={selfReferralID} />
            </NextStepsCard>
          </TodoSectionWrapper>
        </main>
      </section>
      <StartNextSurveyModal
        caseId={selfReferralID}
        showModal={showNextSurveyModal}
        setShowModal={setShowNextSurveyModal}
        continueDataTestid="browser-modal"
        title="Case Created Successfully"
        noNextSurveyText="Please complete the assessment questions for your new case"
      />

      {!isRestricted && !DISABLE_WELCOME_POPUP && (
        <Modal
          open={importantInfo}
          onCancel={handleModalCancel}
          width={768}
          footer={
            <Button onClick={handleModalCancel} type="primary" data-testid="ok">
              {t('OK')}
            </Button>
          }
        >
          <ConfluenceArticle id="CONF_POPUP" />
        </Modal>
      )}
    </div>
  );
};

// Redirect to guest user's first survey
const PatientWrapper = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();

  const { data: { todo } = { todo: [] }, isLoading } = useQuery({
    queryKey: ['getNextSteps'],
    queryFn: async () => {
      const result = await dispatch(actions.getIncompletedToDosList());
      const { todo } = result;
      // set default value if there is no content returned from BE
      return todo ? result : { todo: [] };
    }
  });
  const guestEditableSurveys =
    todo?.filter(
      ({ case_id, todo_type, survey_guest_editable }) =>
        case_id > 0 && todo_type === 'surveyjs' && survey_guest_editable
    ) || [];
  const isRestricted = useIsRestricted();
  const { push, query, pathname } = useRouter();

  if (isLoading) return null;
  const { afterLogin: immediatelyAfterLogin, ...newQuery } = query;

  // only remove "afterLogin" query param if it is present. Stops clobbering query params for navigation "in-flight".
  if (immediatelyAfterLogin) {
    push({ pathname, query: newQuery }, undefined, { shallow: true });
  }

  if (isRestricted && guestEditableSurveys?.length === 1 && immediatelyAfterLogin) {
    push(`/patients/assessments/?surveyjs_id=${guestEditableSurveys[0].survey_id}`);
    return null;
  }

  return (
    <>
      <DocmanGPRejectedModal user={user} />
      <Patients />
    </>
  );
};


export const Route = createFileRoute('/patients/')({ component: PatientWrapper });
